import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useService,
  useUpdateService,
  useDeleteService,
  useDeployService,
} from '@/hooks/useServices'
import { useApproveDeploy, useSkipDeploy, useRedeploy } from '@/hooks/useDeploys'
import {
  useRestartContainer,
  useStopContainer,
  useStartContainer,
} from '@/hooks/useNodes'
import { CircleDot } from 'lucide-react'
import { api } from '@/api/client'
import type { DeployRecord, DockerOptions } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormRow,
  Input,
  Select,
  Toggle,
} from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'
import LogViewer from '@/components/LogViewer'
import { serviceSourceURL } from '@/lib/imageSource'
import { GitHubIcon, DockerIcon } from '@/components/SourceIcons'

// Deploy flow status → Badge variant.
// pending=warning / approved=info / deploying=info / success=success / failed=destructive
const statusVariant: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'info',
  deploying: 'info',
  success: 'success',
  failed: 'destructive',
  skipped: 'muted',
  expired: 'muted',
}

const containerStateVariant: Record<string, BadgeVariant> = {
  running: 'success',
  exited: 'destructive',
  not_found: 'muted',
}

function timeAgo(ts: number): string {
  const sec = Math.floor(Date.now() / 1000 - ts)
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

function duration(d: DeployRecord): string {
  if (!d.finished_at || !d.started_at) return '-'
  const sec = d.finished_at - d.started_at
  if (sec < 60) return `${sec}s`
  return `${Math.floor(sec / 60)}m ${sec % 60}s`
}

// Server returns docker_options as a parsed object; legacy rows may have
// the raw JSON string. Normalize both to a plain object, bail to {} on
// invalid JSON rather than surfacing a parse error to the user.
function parseDockerOpts(raw: DockerOptions | string | undefined | null): DockerOptions {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return raw.trim() ? (JSON.parse(raw) as DockerOptions) : {}
    } catch {
      return {}
    }
  }
  return raw
}

// Newline-separated list <-> array. Empty lines are dropped on save so
// the round-trip stays stable even if the operator leaves trailing
// newlines in the textarea.
function linesToArr(s: string): string[] {
  return s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

function arrToLines(a: string[] | undefined): string {
  return (a ?? []).join('\n')
}

// Whitespace-separated tokens <-> array, with shell-style quoting so
// values that contain spaces can still be expressed inline. Used for
// cmd/entrypoint where pasting `--model /path/foo.gguf` on one line is
// the natural shape and forcing one token per line is friction.
//
// Rules:
//   • Whitespace (any) separates tokens.
//   • '...' preserves content literally (no escapes inside).
//   • "..." preserves content; \" and \\ are escaped, other \X are literal.
//   • \X outside quotes drops the backslash and includes X verbatim
//     (so a literal space can be written as "a\ b" → "a b").
//   • An unterminated quote at EOF is closed implicitly — keeps the form
//     forgiving instead of throwing on every keystroke while typing.
function tokensToArr(s: string): string[] {
  const out: string[] = []
  let cur = ''
  let started = false // distinguishes "" (empty token) from no token
  let mode: 'none' | 'single' | 'double' = 'none'

  const flush = () => {
    if (started) {
      out.push(cur)
      cur = ''
      started = false
    }
  }

  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (mode === 'single') {
      if (ch === "'") mode = 'none'
      else cur += ch
      continue
    }
    if (mode === 'double') {
      if (ch === '"') {
        mode = 'none'
      } else if (ch === '\\' && i + 1 < s.length) {
        const next = s[i + 1]
        if (next === '"' || next === '\\') {
          cur += next
          i++
        } else {
          cur += ch
        }
      } else {
        cur += ch
      }
      continue
    }
    // mode === 'none'
    if (ch === "'") { mode = 'single'; started = true; continue }
    if (ch === '"') { mode = 'double'; started = true; continue }
    if (ch === '\\' && i + 1 < s.length) {
      cur += s[i + 1]
      i++
      started = true
      continue
    }
    if (/\s/.test(ch)) { flush(); continue }
    cur += ch
    started = true
  }
  flush()
  return out
}

// ports map {"8080/tcp":"8080"} <-> "8080/tcp=8080" per line. Accept plain
// "8080=8080" and auto-suffix /tcp so the common case is one pair per
// line without protocol boilerplate.
function linesToPorts(s: string): Record<string, string> {
  const out: Record<string, string> = {}
  for (const raw of s.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const [k, v] = line.split('=').map((p) => p.trim())
    if (!k || !v) continue
    const key = k.includes('/') ? k : `${k}/tcp`
    out[key] = v
  }
  return out
}

function portsToLines(p: Record<string, string> | undefined): string {
  if (!p) return ''
  return Object.entries(p)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n')
}

export default function ServiceDetail() {
  const { id } = useParams()
  const nav = useNavigate()
  const serviceId = id ? Number(id) : undefined
  const { data: svc, isLoading } = useService(serviceId)
  const updateSvc = useUpdateService()
  const deleteSvc = useDeleteService()
  const deploySvc = useDeployService()
  const approve = useApproveDeploy()
  const skip = useSkipDeploy()
  const redeploy = useRedeploy()
  const restart = useRestartContainer()
  const stopC = useStopContainer()
  const startC = useStartContainer()
  const [editing, setEditing] = useState(false)
  const [envRows, setEnvRows] = useState<{ key: string; value: string }[]>([])
  const [jsonMode, setJsonMode] = useState(false)
  const [envText, setEnvText] = useState('')
  const [editingVols, setEditingVols] = useState(false)
  const [volRows, setVolRows] = useState<string[]>([])
  const [logId, setLogId] = useState<number | null>(null)
  // Inline edit for the Service info card (name / image / port / domain).
  // Kept separate from the env/volumes editors so the user can edit one
  // section at a time without committing the others.
  const [editingInfo, setEditingInfo] = useState(false)
  const [infoForm, setInfoForm] = useState({
    name: '',
    image: '',
    container_port: 8080,
    domain: '',
    repo_name: '',
  })
  // Docker runtime options editor — maps to services.docker_options JSON
  // column. We show a structured form for the common fields (shm_size,
  // gpu, healthcheck, privileged, etc.) and a raw-JSON textarea for the
  // list/map fields + anything the structured form doesn't cover.
  const [editingOpts, setEditingOpts] = useState(false)
  const [optsJsonMode, setOptsJsonMode] = useState(false)
  const [optsText, setOptsText] = useState('')
  const [optsForm, setOptsForm] = useState({
    shm_size: '',
    gpu: false,
    privileged: false,
    stdin_open: false,
    tty: false,
    working_dir: '',
    user: '',
    healthcheck_timeout_sec: 0,
    healthcheck_mode: '' as DockerOptions['healthcheck_mode'],
    extra_hosts: '',
    cap_add: '',
    entrypoint: '',
    cmd: '',
    ports: '',
  })
  const qc = useQueryClient()
  const syncEnv = useMutation({
    mutationFn: () => api.syncServiceEnv(serviceId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services'] })
      alert('Environment synced from running container')
    },
    onError: (err: Error) => alert('Sync failed: ' + err.message),
  })

  const { data: deploys } = useQuery({
    queryKey: ['service-deploys', serviceId],
    queryFn: () => api.listServiceDeploys(serviceId!, 30),
    enabled: !!serviceId,
    refetchInterval: 10000,
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-40 bg-muted rounded" />
      </div>
    )
  }

  if (!svc) {
    return <p className="text-sm text-muted-foreground">Service not found.</p>
  }

  const envObj: Record<string, string> = (() => {
    const raw = svc.env_json
    if (typeof raw === 'object' && raw !== null) return raw as Record<string, string>
    if (typeof raw === 'string') { try { return JSON.parse(raw) } catch { /* */ } }
    return {}
  })()

  // volumes_json is *typed* as string[] but legacy / hand-edited rows can
  // hold objects (e.g. {type,name,container} from a stale schema). Coerce
  // every entry to a string so rendering/editing never hits React error
  // #31 ("Objects are not valid as a React child").
  const volumes: string[] = (() => {
    const raw = svc.volumes_json
    let arr: unknown[] = []
    if (Array.isArray(raw)) arr = raw
    else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) arr = parsed
      } catch { /* */ }
    }
    return arr.map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
  })()

  const startEditing = () => {
    const rows = Object.entries(envObj as Record<string, string>).map(([key, value]) => ({ key, value: String(value) }))
    if (rows.length === 0) rows.push({ key: '', value: '' })
    setEnvRows(rows)
    setEnvText(JSON.stringify(envObj, null, 2))
    setJsonMode(false)
    setEditing(true)
  }

  const updateRow = (idx: number, field: 'key' | 'value', val: string) => {
    setEnvRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: val } : r))
  }

  const addRow = () => setEnvRows(rows => [...rows, { key: '', value: '' }])

  const removeRow = (idx: number) => setEnvRows(rows => rows.filter((_, i) => i !== idx))

  const switchToJson = () => {
    const obj: Record<string, string> = {}
    for (const r of envRows) { if (r.key.trim()) obj[r.key.trim()] = r.value }
    setEnvText(JSON.stringify(obj, null, 2))
    setJsonMode(true)
  }

  const switchToRows = () => {
    try {
      const parsed = JSON.parse(envText) as Record<string, string>
      const rows = Object.entries(parsed).map(([key, value]) => ({ key, value: String(value) }))
      if (rows.length === 0) rows.push({ key: '', value: '' })
      setEnvRows(rows)
      setJsonMode(false)
    } catch {
      alert('Invalid JSON — fix it before switching')
    }
  }

  const saveEnv = () => {
    let finalJson: string
    if (jsonMode) {
      try { JSON.parse(envText); finalJson = envText } catch { alert('Invalid JSON'); return }
    } else {
      const obj: Record<string, string> = {}
      for (const r of envRows) { if (r.key.trim()) obj[r.key.trim()] = r.value }
      finalJson = JSON.stringify(obj)
    }
    updateSvc.mutate(
      { id: svc.id, data: { env: JSON.parse(finalJson) } as never },
      { onSuccess: () => setEditing(false) },
    )
  }

  const startEditingVols = () => {
    setVolRows([...volumes])
    setEditingVols(true)
  }

  const saveVols = () => {
    const filtered = volRows.filter(v => v.trim())
    updateSvc.mutate(
      { id: svc.id, data: { volumes: filtered } as never },
      { onSuccess: () => setEditingVols(false) },
    )
  }

  const onDeploy = () => {
    if (confirm(`Deploy ${svc.name} now?`)) {
      deploySvc.mutate(svc.id)
    }
  }

  const onDelete = () => {
    if (confirm(`Delete ${svc.name}? This cannot be undone.`)) {
      deleteSvc.mutate(svc.id, {
        onSuccess: () => nav('/services'),
        onError: (err: Error) => alert('Delete failed: ' + err.message),
      })
    }
  }

  const pendingDeploys = deploys?.filter(d => d.status === 'pending') ?? []

  return (
    <div className="space-y-6">
      {/* Header — on narrow viewports the title + badges can wrap to
          multiple lines and still leave the action buttons inline on the
          right. Mobile gets flex-wrap instead of flex-col so the actions
          aren't buried below a long container name. */}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => nav('/services')}
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline mb-1 block"
          >
            &larr; Services
          </Button>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-foreground break-all">{svc.name}</h1>
            <Badge variant={containerStateVariant[svc.container_state] ?? 'muted'} className="rounded">
              {svc.container_state === 'not_found' ? 'not running' : svc.container_state}
            </Badge>
            {svc.container_status && svc.container_state === 'running' && (
              <span className="text-xs text-muted-foreground">{svc.container_status}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 ml-auto">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onDeploy}
            disabled={deploySvc.isPending}
            loading={deploySvc.isPending}
            className="bg-info text-info-foreground hover:bg-info/90"
          >
            {deploySvc.isPending ? 'Deploying...' : 'Manual Deploy'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onDelete}
            className="text-destructive border-destructive-soft hover:bg-destructive-soft hover:text-destructive"
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Service info — view mode by default, Edit swaps in an inline form
          so the operator can fix the image ref / rename the container /
          retarget the domain without dropping to psql. Save calls the
          existing PUT /api/admin/services/:id handler which already knows
          how to patch name/image/port/domain. */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h2 className="font-semibold text-card-foreground text-sm">Service Info</h2>
          {!editingInfo ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => {
                setInfoForm({
                  name: svc.name,
                  image: svc.image,
                  container_port: svc.container_port,
                  domain: svc.domain,
                  repo_name: svc.repo_name ?? '',
                })
                setEditingInfo(true)
              }}
              className="h-auto p-0 text-xs"
            >
              Edit
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setEditingInfo(false)}
                className="h-auto p-0 text-xs text-muted-foreground no-underline"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="link"
                size="sm"
                disabled={updateSvc.isPending}
                onClick={() => {
                  updateSvc.mutate(
                    {
                      id: svc.id,
                      data: {
                        name: infoForm.name.trim(),
                        image: infoForm.image.trim(),
                        container_port: infoForm.container_port,
                        domain: infoForm.domain.trim(),
                        repo_name: infoForm.repo_name.trim(),
                      } as never,
                    },
                    {
                      onSuccess: () => setEditingInfo(false),
                      onError: (err: Error) => alert('Save failed: ' + err.message),
                    },
                  )
                }}
                className="h-auto p-0 text-xs font-medium no-underline"
              >
                {updateSvc.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
        {editingInfo ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormRow label="Name" stacked>
              <Input
                value={infoForm.name}
                onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })}
              />
            </FormRow>
            <FormRow label="Port" stacked>
              <Input
                type="number"
                value={infoForm.container_port}
                onChange={(e) => setInfoForm({ ...infoForm, container_port: Number(e.target.value) })}
              />
            </FormRow>
            <FormRow
              label="Image"
              help="ghcr.io/<org>/<repo>:tag for our private builds, or any public Docker Hub ref (redis:7, bitnami/postgresql:16, nginx:1.25)."
              stacked
              className="sm:col-span-2"
            >
              <Input
                placeholder="ghcr.io/pentarab/my-app:latest  ·  redis:7-alpine"
                className="font-mono"
                value={infoForm.image}
                onChange={(e) => setInfoForm({ ...infoForm, image: e.target.value })}
              />
            </FormRow>
            <FormRow label="Domain" help="Optional — empty means no nginx/DNS record" stacked className="sm:col-span-2">
              <Input
                placeholder="app.pentarab.com"
                value={infoForm.domain}
                onChange={(e) => setInfoForm({ ...infoForm, domain: e.target.value })}
              />
            </FormRow>
            <FormRow
              label="GitHub repo"
              help={
                'Override the GitHub link shown on this service and the repo used for commit lookup. ' +
                'Empty = derive from image (ghcr.io/org/repo:tag → github.com/org/repo). ' +
                'Accepts a bare name (aso-saas → github.com/pentarab/aso-saas), ' +
                'org/name (other-org/repo), or a full URL.'
              }
              stacked
              className="sm:col-span-2"
            >
              <Input
                placeholder="e.g. aso-saas, other-org/repo, or https://github.com/..."
                value={infoForm.repo_name}
                onChange={(e) => setInfoForm({ ...infoForm, repo_name: e.target.value })}
              />
            </FormRow>
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground block text-xs">Image</span>
            <div className="flex items-start gap-1.5">
              <span className="font-mono text-xs break-all">{svc.image}</span>
              {(() => {
                const src = serviceSourceURL(svc)
                if (!src) return null
                return (
                  <a
                    href={src.href}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground"
                    title={src.kind === 'github' ? `View on GitHub: ${src.href}` : `View on Docker Hub: ${src.href}`}
                    aria-label={src.kind === 'github' ? 'View on GitHub' : 'View on Docker Hub'}
                  >
                    {src.kind === 'github'
                      ? <GitHubIcon className="w-3.5 h-3.5" />
                      : <DockerIcon className="w-3.5 h-3.5" />
                    }
                  </a>
                )
              })()}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs">Port</span>
            <span>{svc.container_port}</span>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs">Domain</span>
            {svc.domain ? (
              <a href={`https://${svc.domain}`} target="_blank" rel="noreferrer"
                className="text-info hover:underline text-xs break-all">{svc.domain}</a>
            ) : <span className="text-muted-foreground">-</span>}
          </div>
          <div>
            <span className="text-muted-foreground block text-xs">Last Deploy</span>
            {svc.last_deploy ? (
              <span className="text-xs">
                <span className="font-mono">{svc.last_deploy.commit_hash?.slice(0, 7)}</span>
                <span className="text-muted-foreground ml-1">{timeAgo(svc.last_deploy.started_at)}</span>
              </span>
            ) : <span className="text-muted-foreground text-xs">Never</span>}
          </div>
        </div>
        )}
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-foreground">Exclusive deploy</div>
            <div className="text-xs text-muted-foreground">
              When on, this service cannot be deployed while another exclusive service is deploying.
              Recommended for critical infra (nginx, infra-admin-saas).
            </div>
          </div>
          <div className="ml-4">
            <Toggle
              checked={!!svc.exclusive_deploy}
              onChange={(checked) =>
                updateSvc.mutate({
                  id: svc.id,
                  data: { exclusive_deploy: checked } as never,
                })
              }
              disabled={updateSvc.isPending}
              aria-label="Exclusive deploy"
            />
          </div>
        </div>
        {/* Container lifecycle actions — hidden when the container isn't
            resolvable on the node (not_found / empty state). Uses the
            service name as the docker id since deploys name the container
            after the service. */}
        {svc.container_state && svc.container_state !== 'not_found' && (() => {
          const isRunning = svc.container_state === 'running'
          const isStopped =
            svc.container_state === 'exited' || svc.container_state === 'created'
          const busy = restart.isPending || stopC.isPending || startC.isPending
          return (
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground mr-1">Container:</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy || !isRunning}
                onClick={() => restart.mutate({ id: svc.name, nodeId: svc.node_id })}
                className="bg-info text-info-foreground border-info hover:bg-info/90 hover:text-info-foreground"
              >
                Restart
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={busy || !isRunning}
                onClick={() => stopC.mutate({ id: svc.name, nodeId: svc.node_id })}
              >
                Stop
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={busy || !isStopped}
                onClick={() => startC.mutate({ id: svc.name, nodeId: svc.node_id })}
                className="bg-success text-success-foreground hover:bg-success/90"
              >
                Start
              </Button>
              {busy && (
                <span className="text-xs text-muted-foreground animate-pulse">Processing...</span>
              )}
              {(restart.isError || stopC.isError || startC.isError) && (
                <span className="text-xs text-destructive">Action failed.</span>
              )}
            </div>
          )
        })()}
      </Card>

      {/* Volumes */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-card-foreground text-sm">Volumes</h2>
          {!editingVols ? (
            <Button type="button" variant="link" size="sm" onClick={startEditingVols} className="h-auto p-0 text-xs">
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button type="button" variant="link" size="sm" onClick={() => setEditingVols(false)}
                className="h-auto p-0 text-xs text-muted-foreground no-underline">Cancel</Button>
              <Button type="button" variant="link" size="sm" onClick={saveVols}
                className="h-auto p-0 text-xs font-medium no-underline">Save</Button>
            </div>
          )}
        </div>
        <div className="p-4">
          {editingVols ? (
            <div className="space-y-2">
              {volRows.map((vol, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    className="flex-1 h-auto px-2 py-1.5 text-xs font-mono"
                    placeholder="volume_name:/container/path or /host/path:/container/path:ro"
                    value={vol}
                    onChange={e => setVolRows(rows => rows.map((r, i) => i === idx ? e.target.value : r))}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setVolRows(rows => rows.filter((_, i) => i !== idx))}
                    className="h-auto px-1 text-xs text-destructive/70 hover:text-destructive"
                  >
                    x
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setVolRows(rows => [...rows, ''])}
                className="h-auto p-0 text-xs"
              >
                + Add volume
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {volumes.map((v, i) => (
                <div key={i} className="font-mono text-xs bg-muted px-2 py-1 rounded break-all">{v}</div>
              ))}
              {volumes.length === 0 && (
                <p className="text-xs text-muted-foreground">No volumes. Click Edit to add.</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Docker runtime options — shm_size / gpu / privileged / entrypoint
          / cap_add / ports / extra_hosts / healthcheck timeout. Mirrors
          the DockerOptions struct in internal/docker/deploy.go. Structured
          form for the common fields; JSON toggle for raw editing. */}
      {(() => {
        const opts = parseDockerOpts(svc.docker_options)
        const nonEmpty = Object.entries(opts).filter(([, v]) => {
          if (v === undefined || v === null || v === '' || v === 0 || v === false) return false
          if (Array.isArray(v) && v.length === 0) return false
          if (typeof v === 'object' && v !== null && Object.keys(v).length === 0) return false
          return true
        })

        const startEdit = () => {
          setOptsForm({
            shm_size: opts.shm_size ?? '',
            gpu: !!opts.gpu,
            privileged: !!opts.privileged,
            stdin_open: !!opts.stdin_open,
            tty: !!opts.tty,
            working_dir: opts.working_dir ?? '',
            user: opts.user ?? '',
            healthcheck_timeout_sec: opts.healthcheck_timeout_sec ?? 0,
            healthcheck_mode: opts.healthcheck_mode ?? '',
            extra_hosts: arrToLines(opts.extra_hosts),
            cap_add: arrToLines(opts.cap_add),
            entrypoint: arrToLines(opts.entrypoint),
            cmd: arrToLines(opts.cmd),
            ports: portsToLines(opts.ports),
          })
          setOptsText(JSON.stringify(opts, null, 2))
          setOptsJsonMode(false)
          setEditingOpts(true)
        }

        const collectForm = (): DockerOptions => {
          const out: DockerOptions = {}
          if (optsForm.shm_size.trim()) out.shm_size = optsForm.shm_size.trim()
          if (optsForm.gpu) out.gpu = true
          if (optsForm.privileged) out.privileged = true
          if (optsForm.stdin_open) out.stdin_open = true
          if (optsForm.tty) out.tty = true
          if (optsForm.working_dir.trim()) out.working_dir = optsForm.working_dir.trim()
          if (optsForm.user.trim()) out.user = optsForm.user.trim()
          if (optsForm.healthcheck_timeout_sec > 0) out.healthcheck_timeout_sec = optsForm.healthcheck_timeout_sec
          if (optsForm.healthcheck_mode) out.healthcheck_mode = optsForm.healthcheck_mode
          const eh = linesToArr(optsForm.extra_hosts)
          if (eh.length) out.extra_hosts = eh
          const ca = linesToArr(optsForm.cap_add)
          if (ca.length) out.cap_add = ca
          const ep = tokensToArr(optsForm.entrypoint)
          if (ep.length) out.entrypoint = ep
          const cm = tokensToArr(optsForm.cmd)
          if (cm.length) out.cmd = cm
          const ports = linesToPorts(optsForm.ports)
          if (Object.keys(ports).length) out.ports = ports
          return out
        }

        const switchToJson = () => {
          setOptsText(JSON.stringify(collectForm(), null, 2))
          setOptsJsonMode(true)
        }
        const switchToForm = () => {
          try {
            const parsed = JSON.parse(optsText || '{}') as DockerOptions
            setOptsForm({
              shm_size: parsed.shm_size ?? '',
              gpu: !!parsed.gpu,
              privileged: !!parsed.privileged,
              stdin_open: !!parsed.stdin_open,
              tty: !!parsed.tty,
              working_dir: parsed.working_dir ?? '',
              user: parsed.user ?? '',
              healthcheck_timeout_sec: parsed.healthcheck_timeout_sec ?? 0,
              healthcheck_mode: parsed.healthcheck_mode ?? '',
              extra_hosts: arrToLines(parsed.extra_hosts),
              cap_add: arrToLines(parsed.cap_add),
              entrypoint: arrToLines(parsed.entrypoint),
              cmd: arrToLines(parsed.cmd),
              ports: portsToLines(parsed.ports),
            })
            setOptsJsonMode(false)
          } catch (e) {
            alert('Invalid JSON: ' + (e as Error).message)
          }
        }

        const save = () => {
          let payload: DockerOptions = {}
          if (optsJsonMode) {
            try {
              payload = JSON.parse(optsText || '{}') as DockerOptions
            } catch (e) {
              alert('Invalid JSON: ' + (e as Error).message)
              return
            }
          } else {
            payload = collectForm()
          }
          updateSvc.mutate(
            { id: svc.id, data: { docker_options: payload } as never },
            {
              onSuccess: () => setEditingOpts(false),
              onError: (err: Error) => alert('Save failed: ' + err.message),
            },
          )
        }

        return (
          <Card>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-card-foreground text-sm">Docker Options</h2>
              {!editingOpts ? (
                <Button type="button" variant="link" size="sm" onClick={startEdit} className="h-auto p-0 text-xs">
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={optsJsonMode ? switchToForm : switchToJson}
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline"
                  >
                    {optsJsonMode ? 'Form' : 'JSON'}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setEditingOpts(false)}
                    className="h-auto p-0 text-xs text-muted-foreground no-underline"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={save}
                    disabled={updateSvc.isPending}
                    className="h-auto p-0 text-xs font-medium no-underline"
                  >
                    {updateSvc.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
            <div className="p-4">
              {editingOpts ? (
                optsJsonMode ? (
                  <textarea
                    rows={14}
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                    value={optsText}
                    onChange={(e) => setOptsText(e.target.value)}
                    placeholder='{"shm_size":"8g","gpu":true,"extra_hosts":["host.docker.internal:host-gateway"]}'
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormRow label="shm_size" help="e.g. '8g', '512m'" stacked>
                      <Input
                        className="font-mono"
                        placeholder="8g"
                        value={optsForm.shm_size}
                        onChange={(e) => setOptsForm({ ...optsForm, shm_size: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="healthcheck_timeout_sec" help="Default 300. Raise for slow GPU/ML containers." stacked>
                      <Input
                        type="number"
                        value={optsForm.healthcheck_timeout_sec}
                        onChange={(e) =>
                          setOptsForm({ ...optsForm, healthcheck_timeout_sec: Number(e.target.value) })
                        }
                      />
                    </FormRow>
                    <FormRow
                      label="healthcheck_mode"
                      help="auto = HTTP probe (our SaaS images). running = third-party images without HEALTHCHECK (ollama, redis, postgres). tcp = TCP connect only. http = force HTTP. none = skip (risky)."
                      stacked
                    >
                      <Select
                        value={optsForm.healthcheck_mode || ''}
                        onValueChange={(v) =>
                          setOptsForm({
                            ...optsForm,
                            healthcheck_mode: (v || '') as DockerOptions['healthcheck_mode'],
                          })
                        }
                        options={[
                          { value: '', label: 'auto (default)' },
                          { value: 'running', label: 'running (third-party images)' },
                          { value: 'tcp', label: 'tcp (TCP connect only)' },
                          { value: 'http', label: 'http (force HTTP probe)' },
                          { value: 'none', label: 'none (skip — risky)' },
                        ]}
                      />
                    </FormRow>
                    <FormRow label="working_dir" stacked>
                      <Input
                        className="font-mono"
                        placeholder="/app"
                        value={optsForm.working_dir}
                        onChange={(e) => setOptsForm({ ...optsForm, working_dir: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="user" stacked>
                      <Input
                        className="font-mono"
                        placeholder="1000:1000"
                        value={optsForm.user}
                        onChange={(e) => setOptsForm({ ...optsForm, user: e.target.value })}
                      />
                    </FormRow>
                    <FormRow
                      label="ports"
                      help="One mapping per line. container=host (e.g. 8080=8080, 443/tcp=443)"
                      stacked
                      className="sm:col-span-2"
                    >
                      <textarea
                        rows={3}
                        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder={'8080=8080\n8188/tcp=8188'}
                        value={optsForm.ports}
                        onChange={(e) => setOptsForm({ ...optsForm, ports: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="extra_hosts" help="One per line. host:ip" stacked className="sm:col-span-2">
                      <textarea
                        rows={2}
                        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder="host.docker.internal:host-gateway"
                        value={optsForm.extra_hosts}
                        onChange={(e) => setOptsForm({ ...optsForm, extra_hosts: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="cap_add" help="One capability per line" stacked>
                      <textarea
                        rows={2}
                        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder="NET_ADMIN"
                        value={optsForm.cap_add}
                        onChange={(e) => setOptsForm({ ...optsForm, cap_add: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="entrypoint" help={'Whitespace-separated — quote args that contain spaces, e.g. --prompt "hello world"'} stacked>
                      <textarea
                        rows={2}
                        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder={'/bin/sh -c'}
                        value={optsForm.entrypoint}
                        onChange={(e) => setOptsForm({ ...optsForm, entrypoint: e.target.value })}
                      />
                    </FormRow>
                    <FormRow label="cmd" help={'Whitespace-separated — quote args that contain spaces, e.g. --prompt "hello world"'} stacked className="sm:col-span-2">
                      <textarea
                        rows={2}
                        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder={'--model /models/foo.gguf --host 0.0.0.0'}
                        value={optsForm.cmd}
                        onChange={(e) => setOptsForm({ ...optsForm, cmd: e.target.value })}
                      />
                    </FormRow>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={optsForm.gpu}
                        onChange={(e) => setOptsForm({ ...optsForm, gpu: e.target.checked })}
                      />
                      <span>gpu (request all GPUs)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={optsForm.privileged}
                        onChange={(e) => setOptsForm({ ...optsForm, privileged: e.target.checked })}
                      />
                      <span>privileged</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={optsForm.stdin_open}
                        onChange={(e) => setOptsForm({ ...optsForm, stdin_open: e.target.checked })}
                      />
                      <span>stdin_open</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={optsForm.tty}
                        onChange={(e) => setOptsForm({ ...optsForm, tty: e.target.checked })}
                      />
                      <span>tty</span>
                    </label>
                  </div>
                )
              ) : nonEmpty.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No docker options set. Click Edit to configure shm_size, gpu, extra_hosts, etc.
                </p>
              ) : (
                <div className="space-y-1.5 sm:space-y-1">
                  {nonEmpty.map(([k, v]) => (
                    <div
                      key={k}
                      className="text-xs flex flex-col gap-0.5 sm:flex-row sm:gap-2 sm:items-baseline border-b border-border/40 sm:border-0 pb-1 sm:pb-0 last:border-0"
                    >
                      <span className="font-mono font-medium text-foreground sm:min-w-[200px] sm:shrink-0">
                        {k}
                      </span>
                      <span className="font-mono text-muted-foreground break-all sm:truncate sm:flex-1 sm:min-w-0">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )
      })()}

      {/* Pending approvals are now rendered inline in the Deploy History
          card below — keeping a separate panel on top duplicated the same
          Skip/Deploy buttons. The header badge there surfaces the count. */}

      {/* Environment variables */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-card-foreground text-sm">Environment Variables</h2>
          {!editing ? (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => syncEnv.mutate()}
                disabled={syncEnv.isPending}
                loading={syncEnv.isPending}
                className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline"
                title="Read env from running container"
              >
                {syncEnv.isPending ? 'Syncing...' : 'Sync from container'}
              </Button>
              <Button type="button" variant="link" size="sm" onClick={startEditing}
                className="h-auto p-0 text-xs">
                Edit
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={jsonMode ? switchToRows : switchToJson}
                className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline"
              >
                {jsonMode ? 'Key-Value' : 'JSON'}
              </Button>
              <Button type="button" variant="link" size="sm" onClick={() => setEditing(false)}
                className="h-auto p-0 text-xs text-muted-foreground no-underline">Cancel</Button>
              <Button type="button" variant="link" size="sm" onClick={saveEnv}
                className="h-auto p-0 text-xs font-medium no-underline">Save</Button>
            </div>
          )}
        </div>
        <div className="p-4">
          {editing ? (
            jsonMode ? (
              <textarea
                rows={14}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                value={envText}
                onChange={e => setEnvText(e.target.value)}
                placeholder='{"KEY": "value", ...}'
              />
            ) : (
              <div className="space-y-2">
                {envRows.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col gap-1 sm:flex-row sm:gap-2 sm:items-center border-b border-border/40 sm:border-0 pb-2 sm:pb-0 last:border-0"
                  >
                    <Input
                      block={false}
                      className="w-full sm:w-[200px] sm:shrink-0 h-auto px-2 py-1.5 text-xs font-mono"
                      placeholder="KEY"
                      value={row.key}
                      onChange={e => updateRow(idx, 'key', e.target.value)}
                    />
                    <span className="hidden sm:inline text-muted-foreground">=</span>
                    <div className="flex gap-2 items-center w-full sm:flex-1 sm:min-w-0">
                      <Input
                        block={false}
                        className="flex-1 min-w-0 h-auto px-2 py-1.5 text-xs font-mono"
                        placeholder="value"
                        value={row.value}
                        onChange={e => updateRow(idx, 'value', e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRow(idx)}
                        className="h-auto px-1 text-xs text-destructive/70 hover:text-destructive shrink-0"
                        title="Remove"
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={addRow}
                  className="h-auto p-0 text-xs"
                >
                  + Add variable
                </Button>
              </div>
            )
          ) : (
            <div className="space-y-1.5 sm:space-y-1">
              {Object.entries(envObj as Record<string, string>).map(([k, v]) => (
                <div
                  key={k}
                  className="text-xs flex flex-col gap-0.5 sm:flex-row sm:gap-2 sm:items-baseline border-b border-border/40 sm:border-0 pb-1 sm:pb-0 last:border-0"
                >
                  <span className="font-mono font-medium text-foreground sm:min-w-[200px] sm:shrink-0 break-all">
                    {k}
                  </span>
                  <span className="font-mono text-muted-foreground break-all sm:truncate sm:flex-1 sm:min-w-0">
                    {String(v)}
                  </span>
                </div>
              ))}
              {Object.keys(envObj).length === 0 && (
                <p className="text-xs text-muted-foreground">No environment variables set. Click Edit to add.</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Container logs — only shown when the container exists on the node.
          Uses service name as the container id since deploys name containers
          after the service. 'exited' containers still have readable logs,
          so we include them too — only hide when the container is absent. */}
      {svc.container_state && svc.container_state !== 'not_found' && (
        <LogViewer containerId={svc.name} nodeId={svc.node_id} />
      )}

      {/* Deploy history — mirrors the Deploy page's history panel so the
          operator can approve / skip / redeploy from here without bouncing
          to a different route. Rows are grouped by commit_hash (redeploys
          of the same commit fold into one row with a ↻ N badge). */}
      {(() => {
        if (!deploys) {
          return (
            <Card>
              <div className="px-4 py-3 border-b border-border">
                <h2 className="font-semibold text-card-foreground text-sm">Deploy History</h2>
              </div>
              <div className="px-4 py-6 text-center text-muted-foreground text-xs animate-pulse">Loading history...</div>
            </Card>
          )
        }
        // Group by commit_hash (empty hash rows stay individual)
        type Group = { rep: DeployRecord; count: number; members: DeployRecord[] }
        const grouped: Group[] = []
        const keyIndex = new Map<string, number>()
        for (const d of deploys) {
          const key = d.commit_hash ? `c:${d.commit_hash}` : `i:${d.id}`
          const idx = keyIndex.get(key)
          if (idx === undefined) {
            keyIndex.set(key, grouped.length)
            grouped.push({ rep: d, count: 0, members: [d] })
          } else {
            const g = grouped[idx]
            g.count += 1
            g.members.push(d)
            if (d.id > g.rep.id) g.rep = d
          }
        }
        const currentSuccessId = deploys.reduce<number>(
          (acc, d) => (d.status === 'success' && d.id > acc ? d.id : acc),
          -1,
        )
        const currentRepId = (() => {
          if (currentSuccessId < 0) return -1
          const g = grouped.find((grp) => grp.members.some((m) => m.id === currentSuccessId))
          return g?.rep.id ?? currentSuccessId
        })()

        return (
          <Card>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-card-foreground text-sm">Deploy History</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Click a row to expand deploy log</p>
              </div>
              {pendingDeploys.length > 0 && (
                <Badge variant="warning" className="text-xs rounded font-medium">
                  {pendingDeploys.length} awaiting approval
                </Badge>
              )}
            </div>
            <div className="divide-y divide-border">
              {grouped.length === 0 && (
                <EmptyState
                  description="No deploy history yet. Click &quot;Manual Deploy&quot; to start."
                  className="py-8"
                />
              )}
              {grouped.map(({ rep: d, count }) => {
                const msg = (d.commit_msg || '').trimEnd()
                const nl = msg.indexOf('\n')
                const title = nl < 0 ? msg : msg.slice(0, nl)
                const body = nl < 0 ? '' : msg.slice(nl + 1).replace(/^\n+/, '')
                const isCurrent = d.id === currentRepId
                const isPending = d.status === 'pending'
                const isDeploying = d.status === 'deploying'
                const expanded = logId === d.id
                return (
                  <div key={d.id} className={isCurrent ? 'bg-success-soft/40' : ''}>
                    <div
                      className="px-3 sm:px-4 py-2.5 hover:bg-muted/50 cursor-pointer text-xs flex flex-col gap-1"
                      onClick={() => setLogId(expanded ? null : d.id)}
                    >
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <span className="text-muted-foreground w-4 shrink-0">{expanded ? '▼' : '▶'}</span>
                        {isCurrent && (
                          <Badge
                            variant="info"
                            shape="square"
                            className="shrink-0"
                            title="Currently running"
                            aria-label="Currently running"
                          >
                            <CircleDot size={12} aria-hidden="true" />
                          </Badge>
                        )}
                        <Badge variant={statusVariant[d.status] ?? 'muted'} className="shrink-0 rounded">
                          {d.status}
                        </Badge>
                        <span className="font-mono text-foreground shrink-0">{d.commit_hash?.slice(0, 7) || '-'}</span>
                        {count > 0 && (
                          <Badge
                            variant="muted"
                            className="shrink-0 text-[10px] rounded font-medium"
                            title={`${count} additional run${count === 1 ? '' : 's'} of this commit`}
                          >
                            ↻ {count}
                          </Badge>
                        )}
                        <span
                          className="text-foreground truncate flex-1 min-w-0 basis-full sm:basis-auto order-last sm:order-none"
                          title={msg}
                        >
                          {title || '-'}
                        </span>
                        <span className="hidden sm:inline text-muted-foreground w-12 text-right shrink-0">{duration(d)}</span>
                        <span className="hidden sm:inline text-muted-foreground w-16 text-right shrink-0">{d.triggered_by}</span>
                        <span className="hidden sm:inline text-muted-foreground w-20 text-right shrink-0">{timeAgo(d.started_at)}</span>
                        {isPending ? (
                          <div className="flex items-center gap-1.5 ml-auto sm:ml-2 shrink-0">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); skip.mutate(d.id) }}
                              disabled={skip.isPending}
                              className="h-auto px-2 py-0.5 text-xs text-muted-foreground"
                            >Skip</Button>
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); approve.mutate(d.id) }}
                              disabled={approve.isPending}
                              className="h-auto px-2 py-0.5 text-xs bg-success text-success-foreground hover:bg-success/90"
                            >Deploy</Button>
                          </div>
                        ) : !isDeploying && (
                          <div className="flex items-center ml-auto sm:ml-2 shrink-0">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); redeploy.mutate(d.id) }}
                              disabled={redeploy.isPending}
                              title={
                                d.commit_hash
                                  ? `Redeploy this commit — pulls :${d.commit_hash.slice(0, 7)} (falls back to :latest if the pinned tag wasn't pushed by CI).`
                                  : 'Redeploy this entry — no commit recorded, so this pulls :latest.'
                              }
                              className="h-auto px-2 py-0.5 text-xs text-muted-foreground"
                            >↻ Redeploy</Button>
                          </div>
                        )}
                      </div>
                      {/* Mobile-only meta strip */}
                      <div className="sm:hidden flex items-center gap-2 pl-6 text-[10px] text-muted-foreground">
                        <span>{duration(d)}</span>
                        <span className="opacity-40">·</span>
                        <span>{d.triggered_by}</span>
                        <span className="opacity-40">·</span>
                        <span>{timeAgo(d.started_at)}</span>
                      </div>
                      {body && !expanded && (
                        <div className="pl-6 sm:pl-[4.25rem] text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3" title={body}>
                          {body}
                        </div>
                      )}
                    </div>
                    {/* Terminal log panel — KEPT RAW (CRT aesthetic) */}
                    {expanded && (
                      <div className="bg-black text-green-400 px-4 py-3 text-xs font-mono border border-gray-700 whitespace-pre-wrap max-h-80 overflow-y-auto">
                        {body && (
                          <>
                            <div className="text-gray-400 text-[10px] uppercase tracking-wide mb-1">Commit message</div>
                            <div className="text-gray-200 mb-3">{d.commit_msg}</div>
                          </>
                        )}
                        <div className="text-gray-400 text-[10px] uppercase tracking-wide mb-1">Deploy log</div>
                        {isDeploying && !d.log && (
                          <span className="text-yellow-400 animate-pulse">Deploying in progress...</span>
                        )}
                        {d.log || (!isDeploying && <span className="text-gray-500">(no log recorded)</span>)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        )
      })()}
    </div>
  )
}
